<template>
  <div
    :class="classes.adSliderWrapper"
    @mouseover="mouseOver(true)"
    @mouseleave="mouseOver(false)"
    @click="mouseClick"
  >
    <transition
      name="slide-left"
      :class="[classes.slide]"
      v-show="showPreroll && usePreroll"
    >
      <slot name="preroll" />
    </transition>
    <transition
      name="slide-left"
      :class="[classes.slide]"
      v-visible="!showPreroll || !usePreroll"
    >
      <slot name="adcontent" />
    </transition>
  </div>
</template>
<script lang="ts">
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import { defineComponent, watch, onMounted, ref, computed } from "vue";
import {
  injectPreview,
  PreviewContext
} from "@fresh8/ad-data-composition/src/preview";
import { injectPreroll, PrerollContext } from "../hooks/usePreroll";

const { useStyles } = createUseStyles({
  adSliderWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",
    overflow: "hidden"
  },
  slide: {
    position: "absolute",
    transition: "all 0.5s ease",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    "&.slide-left-enter-from": {
      transition: "none",
      transform: "translateX(-100%)"
    },
    "&.slide-left-leave-to": {
      transform: "translateX(100%)"
    }
  }
});

export default defineComponent({
  setup() {
    const showPreroll = ref(true);
    const {
      usePreroll,
      prerollDuration,
      prerollClicked
    } = injectPreroll() as PrerollContext;

    const previewContext = injectPreview() as PreviewContext;
    const timeout = ref<ReturnType<typeof setTimeout> | undefined>();

    const previewPreroll = computed(
      () => previewContext.preview.value.previewPreroll
    );

    const handleTimeout = cancel => {
      const duration = prerollDuration.value;
      if (cancel && timeout.value !== undefined) {
        clearTimeout(timeout.value);
        timeout.value = undefined;
      } else {
        timeout.value = setTimeout(() => {
          if (!previewPreroll.value) {
            showPreroll.value = false;
          }
        }, 1000 * (duration || 3));
      }
    };

    watch(previewPreroll, newVal => {
      showPreroll.value = newVal;
    });

    // This will only update via the previews,
    // we only want to showPreroll for the preroll specific preview.
    watch(usePreroll, newVal => {
      if (previewPreroll.value) {
        showPreroll.value = newVal;
      }
    });

    const mouseClick = (e: MouseEvent) => {
      if (
        usePreroll.value === true &&
        prerollDuration.value === 0 &&
        prerollClicked.value === false
      ) {
        prerollClicked.value = true;
        e.stopPropagation();
        showPreroll.value = false;
      }
    };

    const mouseOver = bool => {
      if (usePreroll.value === true && prerollDuration.value === 0) return;
      handleTimeout(bool);
    };

    const isPreview = computed(() => previewContext.preview.value.isPreview);

    onMounted(() => {
      if (isPreview.value) {
        showPreroll.value = false;
        return;
      }

      if (usePreroll.value === true && prerollDuration.value === 0) {
        return;
      }
      handleTimeout(false);
    });

    const { classesRef } = useStyles(ref({}));
    return {
      mouseOver,
      mouseClick,
      classes: classesRef,
      showPreroll,
      usePreroll
    };
  }
});
</script>
